/**
 * Virtual keys — wire shapes.
 *
 * A virtual key is the SECOND authenticator: a bearer secret a workload pastes where its SaaS
 * key used to go, so the only change to a running script is two configuration values. It is
 * deliberately weaker than the SDK's per-call proof and every surface here says so — the roster
 * shows the binding plainly rather than letting a reader infer the stronger one from silence.
 *
 * THE SECRET APPEARS IN EXACTLY ONE SHAPE, `createVirtualKeyResponseSchema.key`, and only in the
 * resolved value of the create mutation. It is never in a list response, never in a query cache,
 * never re-fetchable. A lost key is replaced, not recovered.
 */

import { z } from "zod";

/** A key as the roster shows it. Carries no secret — the display prefix is deliberately too short to be one. */
export const virtualKeySchema = z.object({
  id: z.string(),
  agent_id: z.string(),
  standing_delegation_id: z.string(),
  key_prefix: z.string(),
  allowed_destinations: z.array(z.string()),
  label: z.string(),
  created_at: z.string(),
  expires_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
  revoked_reason: z.string(),
  last_used_at: z.string().nullable(),
});

export type VirtualKey = z.infer<typeof virtualKeySchema>;

export const virtualKeysListResponseSchema = z.object({
  virtual_keys: z.array(virtualKeySchema).nullable().transform((v) => v ?? []),
});

export type VirtualKeysListResponse = z.infer<typeof virtualKeysListResponseSchema>;

/**
 * The create response. `key` is the one and only time the secret exists outside the workload.
 *
 * Note what is NOT here and must never be added: a digest, a length, or any prefix of the secret
 * beyond `key_prefix`. A digest is a confirmation oracle for a guessed value.
 */
export const createVirtualKeyResponseSchema = z.object({
  id: z.string(),
  key: z.string(),
  key_prefix: z.string(),
  agent_id: z.string(),
  allowed_destinations: z.array(z.string()),
  label: z.string(),
  expires_at: z.string().nullish(),
});

export type CreateVirtualKeyResponse = z.infer<typeof createVirtualKeyResponseSchema>;

/**
 * The create form.
 *
 * `standing_delegation_id` is REQUIRED and is not defaulted — an agent may hold several standing
 * delegations with different boundaries and different approving humans, so choosing one for the
 * operator would pick the key's authority by implementation detail. The server refuses a missing
 * one for the same reason.
 *
 * No `.default()` anywhere: Zod 4 widens the input type to allow undefined, which breaks RHF's
 * SubmitHandler generic. Defaults belong in the form's defaultValues.
 */
export const createVirtualKeySchema = z.object({
  standing_delegation_id: z.string().min(1, "Choose which authorization this key speaks for."),
  label: z.string().max(200, "Keep the label under 200 characters.").optional(),
  // An empty list is ACCEPTED by the server and means the key reaches nothing. That is a
  // legitimate thing to create (provision now, scope at rollout) and the form says so rather
  // than pushing an operator toward pasting a wide list to make it work.
  allowed_destinations: z.array(z.string().min(1)),
  expires_at: z.string().optional(),
});

export type CreateVirtualKeyInput = z.infer<typeof createVirtualKeySchema>;

/** A revoke needs no reason — making someone compose a sentence before killing a leaked credential costs more than it buys. */
export const revokeVirtualKeySchema = z.object({
  reason: z.string().max(200).optional(),
});

export type RevokeVirtualKeyInput = z.infer<typeof revokeVirtualKeySchema>;

/** The lifecycle state a row is in, derived rather than stored — the server sends timestamps. */
export type VirtualKeyState = "active" | "revoked" | "expired";

export function virtualKeyState(key: VirtualKey, now: Date = new Date()): VirtualKeyState {
  if (key.revoked_at) return "revoked";
  if (key.expires_at && new Date(key.expires_at) <= now) return "expired";
  return "active";
}
